import { useQuery } from "@apollo/client";
import { differenceInDays } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  CognitiveDomain as GraphQLCognitiveDomain,
  GetAllActivitiesQuery,
  GetAllActivitiesQueryVariables,
  StatusView as GraphQLStatusView,
} from "@/graphql/graphql";
import { useSpecialistDropdown } from "@/hooks/useSpecialistDropDown";
import { AppPageNoMaxWidth } from "@/layouts";
import { convertActivationStatus } from "@/transformers/account.transformer";
import { CognitiveDomain, StatusView } from "@/types/api";
import { getEnumName } from "@/types/utils";
import { convertStringToDate, transformDate } from "@/utils/date-utils";

import { ActivityDetailModal } from "./components/ActivityDetailModal";
import { ActivityLibraryDataTable } from "./components/ActivityLibraryTable";
import { DataRow } from "./components/ActivityLibraryTable/types";
import { ActivityUpload } from "./components/ActivityUpload";
import { ActivitySidebar } from "./components/Sidebar";
import { GET_ALL_ACTIVITIES } from "./queries";

export const VoiceMessagesLibrary = () => {
  const [filterCogDomain, setfilterCogDomain] = useState<CognitiveDomain | undefined>(undefined);
  const [filterCategory, setfilterCategory] = useState<string | undefined>(undefined);
  const [activityFilterStatus, setActivityFilterStatus] = useState<string>("Active");
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);
  const [activityId, setActivityId] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const handleClose = () => setOpen(false);

  const { options: specialistOptions, loading: loadingSpecialists } = useSpecialistDropdown();

  const specialistNameMap = useMemo(() => {
    const map = new Map<number, string>();
    specialistOptions.forEach((specialist) => {
      map.set(specialist.value, specialist.label);
    });
    return map;
  }, [specialistOptions]);

  const getSpecialistName = useCallback(
    (id: number | undefined) => {
      if (!id) {
        return "-";
      }
      return specialistNameMap.get(id) || "-";
    },
    [specialistNameMap]
  );

  const { data, error, loading, refetch } = useQuery<
    GetAllActivitiesQuery,
    GetAllActivitiesQueryVariables
  >(GET_ALL_ACTIVITIES, {
    fetchPolicy: "network-only",
    variables: {
      inLibrary: true,
      statusView: getEnumName(StatusView, activityFilterStatus) as GraphQLStatusView,
      cognitiveDomain: getEnumName(CognitiveDomain, filterCogDomain) as GraphQLCognitiveDomain,
    },
  });

  useEffect(() => {
    refetch({ statusView: getEnumName(StatusView, activityFilterStatus) as GraphQLStatusView });
  }, [activityFilterStatus, refetch]);

  const activityLibrary = useMemo(
    () => data?.voiceMessages?.data ?? [],
    [data?.voiceMessages?.data]
  );

  const tableData = useMemo(() => {
    if (loadingSpecialists) {
      return [];
    }

    return activityLibrary.map((activity) => {
      const activityStatus = convertActivationStatus(activity?.deletedAt);
      const createdDate = convertStringToDate(activity?.createdOn);
      const isNew = createdDate ? differenceInDays(new Date(), createdDate) <= 14 : false;
      const recorderName = getSpecialistName(activity?.recordedBy?.id);

      const allFields: string = [
        activity?.title,
        transformDate(activity?.createdOn),
        activity?.feature,
        activity?.cognitiveDomains?.join(" "),
        activityStatus,
        isNew ? "new" : "old",
        recorderName,
        activity?.difficulty,
      ]
        .map((x) => x ?? "")
        .join(" ")
        .toLowerCase();

      return {
        id: activity?.id,
        title: activity?.title,
        dateRecorded: transformDate(activity?.createdOn),
        fullISODate: createdDate?.toISOString() ?? "",
        cognitiveDomains: activity?.cognitiveDomains ?? [],
        status: activityStatus,
        allFields,
        newlyCreated: isNew ? "New" : "",
        recorder: recorderName,
        difficulty: activity?.difficulty,
        feature: activity?.feature,
      };
    });
  }, [activityLibrary, getSpecialistName, loadingSpecialists]);

  const onRowClick = (row: DataRow) => {
    setOpen(true);
    setActivityId(row.id);
  };

  const onActivityCreated = (id: number) => {
    setOpen(true);
    setActivityId(id);
  };

  const isLoading = loading || loadingSpecialists;

  return (
    <AppPageNoMaxWidth>
      <ActivitySidebar
        setCogDomainFilter={setfilterCogDomain}
        setCategoryFilter={setfilterCategory}
      />
      <div style={{ width: "100%" }}>
        <ActivityUpload onActivityCreated={onActivityCreated} />
        <ActivityLibraryDataTable
          categoryFilter={filterCategory}
          tableData={tableData as DataRow[]}
          error={error}
          loading={isLoading}
          onRowClick={onRowClick}
          setActivityFilterStatus={setActivityFilterStatus}
          activityFilterStatus={activityFilterStatus}
          difficultyFilter={difficultyFilter}
          setDifficultyFilter={setDifficultyFilter}
        />
      </div>
      <ActivityDetailModal
        setActivityFilterStatus={setActivityFilterStatus}
        activityLibrary={activityLibrary}
        open={open}
        onClose={handleClose}
        activityId={activityId}
      />
    </AppPageNoMaxWidth>
  );
};
